import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { EventInstance } from '@pacer/shared';
import { MEMBER_COLORS } from '@pacer/shared';
import * as api from '../api';
import { EventComments } from '../components/EventComments';
import { Icon, type IconName } from '../components/Icon';
import { Modal } from '../components/Modal';
import { Avatar, Button, Field, Input, Select, Textarea, cx } from '../components/ui';
import { fromLocalInput, toLocalInput, useZone } from '../lib/dates';
import { useActiveMemberId } from '../state/activeMember';
import { queryKeys } from '../state/queries';
import { confirmDelete, toast } from '../state/ui';

const REPEATS = [
  { label: 'Does not repeat', value: '' },
  { label: 'Every day', value: 'FREQ=DAILY' },
  { label: 'Every week', value: 'FREQ=WEEKLY' },
  { label: 'Every 2 weeks', value: 'FREQ=WEEKLY;INTERVAL=2' },
  { label: 'Every month', value: 'FREQ=MONTHLY' },
  { label: 'Every year', value: 'FREQ=YEARLY' },
];

/**
 * Create/edit an event. Editing a recurring occurrence edits the whole series — the one
 * per-occurrence action offered is "skip", which covers the case that actually comes up
 * (practice cancelled this week) without the complexity of splitting a series.
 */
export function EventEditor({
  instance,
  defaultDay,
  onClose,
}: {
  instance?: EventInstance;
  defaultDay?: string;
  onClose: () => void;
}) {
  const zone = useZone();
  const qc = useQueryClient();
  const activeId = useActiveMemberId();
  const isEdit = Boolean(instance);
  const readOnly = instance?.readOnly === true;

  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });
  const { data: categories = [] } = useQuery({ queryKey: queryKeys.categories, queryFn: api.getCategories });

  // A feed event has no editable record behind it, so don't try to load one.
  const { data: record } = useQuery({
    queryKey: ['event', instance?.eventId],
    queryFn: () => api.getEvent(instance!.eventId),
    enabled: Boolean(instance) && !readOnly,
  });

  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [rrule, setRrule] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [reminderMinutes, setReminderMinutes] = useState<number | null>(null);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [scopePrompt, setScopePrompt] = useState(false);

  useEffect(() => {
    if (record) {
      setTitle(record.title);
      setLocation(record.location ?? '');
      setNotes(record.description ?? '');
      setAllDay(record.allDay);
      setStart(toLocalInput(record.startTs, zone));
      setEnd(toLocalInput(record.endTs, zone));
      setRrule(record.rrule ?? '');
      setColor(record.color);
      setCategoryId(record.categoryId);
      setReminderMinutes(record.reminderMinutes);
      setMemberIds(record.memberIds);
      return;
    }
    if (instance) return; // editing, record still loading

    const base = defaultDay
      ? DateTime.fromISO(defaultDay, { zone }).set({ hour: 9 })
      : DateTime.now().setZone(zone).plus({ hours: 1 }).startOf('hour');
    setStart(base.toFormat("yyyy-LL-dd'T'HH:mm"));
    setEnd(base.plus({ hours: 1 }).toFormat("yyyy-LL-dd'T'HH:mm"));
    setMemberIds(activeId ? [activeId] : []);
  }, [record, instance, defaultDay, zone, activeId]);

  // Keep the end from landing before the start as the user edits.
  useEffect(() => {
    if (start && end && end < start) {
      setEnd(DateTime.fromISO(start).plus({ hours: 1 }).toFormat("yyyy-LL-dd'T'HH:mm"));
    }
  }, [start, end]);

  const buildPayload = () => ({
    title: title.trim(),
    description: notes.trim() || null,
    location: location.trim() || null,
    // An all-day event runs local midnight → exclusive next midnight.
    startTs: allDay
      ? DateTime.fromISO(start, { zone }).startOf('day').toUTC().toISO()!
      : fromLocalInput(start, zone),
    endTs: allDay
      ? DateTime.fromISO(end, { zone }).startOf('day').plus({ days: 1 }).toUTC().toISO()!
      : fromLocalInput(end, zone),
    allDay,
    rrule: rrule || null,
    color,
    categoryId,
    reminderMinutes,
    memberIds,
  });

  const afterWrite = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['events'] });
    onClose();
  };

  const save = useMutation({
    mutationFn: async () =>
      instance
        ? api.updateEvent(instance.eventId, buildPayload())
        : api.createEvent({ ...buildPayload(), createdBy: activeId }),
    onSuccess: afterWrite,
    onError: (e: Error) => setError(e.message),
  });

  // "This event only" — split the occurrence off the series (detached instance).
  const detach = useMutation({
    mutationFn: () =>
      // The detached instance is a one-off, so it never carries the series' repeat rule.
      api.detachOccurrence(instance!.eventId, instance!.occurrenceTs!, {
        ...buildPayload(),
        rrule: null,
        createdBy: activeId,
      }),
    onSuccess: async () => {
      toast('This event updated', 'success');
      await afterWrite();
    },
    onError: (e: Error) => setError(e.message),
  });

  // A recurring occurrence prompts for scope on save; everything else saves straight away.
  const isRecurringOccurrence = Boolean(instance?.isRecurring && instance?.occurrenceTs);
  const onSave = (): void => {
    if (isRecurringOccurrence) setScopePrompt(true);
    else save.mutate();
  };

  const remove = useMutation({
    mutationFn: () => api.deleteEvent(instance!.eventId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['events'] });
      toast('Event deleted', 'success');
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const askDelete = async (): Promise<void> => {
    const ok = await confirmDelete(
      instance!.isRecurring ? 'this repeating event' : 'this event',
      instance!.isRecurring ? 'This removes the whole series. To drop just one day, use “Skip this one”.' : undefined,
    );
    if (ok) remove.mutate();
  };

  const skip = useMutation({
    mutationFn: () => api.skipOccurrence(instance!.eventId, instance!.occurrenceTs!),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['events'] });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  if (readOnly) {
    return (
      <Modal open onClose={onClose} eyebrow={instance!.feedName ?? 'Subscribed'} title={instance!.title}>
        <div className="flex flex-col gap-3">
          {instance!.location && (
            <p className="text-[14px] text-secondary">{instance!.location}</p>
          )}
          {instance!.description && (
            <p className="text-[13px] whitespace-pre-wrap text-secondary">{instance!.description}</p>
          )}
          <p className="font-mono text-[12px] leading-relaxed text-muted">
            This comes from a subscribed calendar and is read-only here. Change it where it lives
            and it'll update on the next sync.
          </p>
        </div>
      </Modal>
    );
  }

  const dateType = allDay ? 'date' : 'datetime-local';
  const trimDate = (v: string): string => (allDay ? v.slice(0, 10) : v);

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={isEdit ? 'Edit' : 'New'}
      title={isEdit ? 'Edit event' : 'New event'}
      size="lg"
      footer={
        <>
          {isEdit && (
            <Button variant="danger" onClick={askDelete} disabled={remove.isPending}>
              Delete
            </Button>
          )}
          {isEdit && instance?.isRecurring && instance.occurrenceTs && (
            <Button variant="secondary" onClick={() => skip.mutate()} disabled={skip.isPending}>
              Skip this one
            </Button>
          )}
          <Button
            variant="primary"
            className="ml-auto min-w-28"
            onClick={onSave}
            disabled={!title.trim() || save.isPending || detach.isPending}
          >
            {save.isPending || detach.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          autoFocus
          placeholder="What's happening?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          enterKeyHint="done"
          autoCapitalize="sentences"
          className="!text-[18px]"
        />

        <label className="flex touch items-center gap-3">
          <input
            type="checkbox"
            checked={allDay}
            onChange={(e) => setAllDay(e.target.checked)}
            className="size-5 accent-[var(--color-accent)]"
          />
          <span className="text-[14px] text-secondary">All day</span>
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Starts">
            <Input type={dateType} value={trimDate(start)} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field label="Ends">
            <Input type={dateType} value={trimDate(end)} onChange={(e) => setEnd(e.target.value)} />
          </Field>
        </div>

        <Field label="Repeats">
          <Select value={rrule} onChange={(e) => setRrule(e.target.value)}>
            {REPEATS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
            {/* A rule set elsewhere (or hand-written) must survive an edit here. */}
            {rrule && !REPEATS.some((r) => r.value === rrule) && (
              <option value={rrule}>Custom ({rrule})</option>
            )}
          </Select>
        </Field>

        <Field label="Remind" hint="Sends a push to devices with notifications on.">
          <Select
            value={reminderMinutes === null ? '' : String(reminderMinutes)}
            onChange={(e) => setReminderMinutes(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">No reminder</option>
            <option value="0">At start time</option>
            <option value="10">10 minutes before</option>
            <option value="30">30 minutes before</option>
            <option value="60">1 hour before</option>
          </Select>
        </Field>

        {categories.length > 0 && (
          <Field label="Category">
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                onClick={() => setCategoryId(null)}
                aria-pressed={categoryId === null}
                className={cx(
                  'h-9 rounded-full border px-3 text-[13px] transition-colors',
                  categoryId === null ? 'border-border text-primary' : 'border-border-subtle text-muted',
                )}
              >
                None
              </button>
              {categories.map((cat) => {
                const on = categoryId === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setCategoryId(on ? null : cat.id)}
                    aria-pressed={on}
                    className="flex items-center gap-1.5 rounded-full border px-3 text-[13px] transition-colors"
                    style={{
                      height: 36,
                      borderColor: on ? cat.color : 'var(--color-border)',
                      background: on ? `color-mix(in srgb, ${cat.color} 14%, transparent)` : 'transparent',
                      color: on ? cat.color : 'var(--color-text-secondary)',
                    }}
                  >
                    {cat.icon && <Icon name={cat.icon as IconName} size={13} />}
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </Field>
        )}

        <Field label="Who">
          <div className="flex flex-wrap gap-2 pt-1">
            {members.length === 0 && (
              <span className="font-mono text-[12px] text-muted">Add family members in Settings.</span>
            )}
            {members.map((m) => {
              const on = memberIds.includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() =>
                    setMemberIds((ids) => (on ? ids.filter((i) => i !== m.id) : [...ids, m.id]))
                  }
                  aria-pressed={on}
                  className="flex items-center gap-2 rounded-full border py-1 pr-3 pl-1 transition-colors"
                  style={{
                    borderColor: on ? m.color : 'var(--color-border)',
                    background: on ? `color-mix(in srgb, ${m.color} 12%, transparent)` : 'transparent',
                  }}
                >
                  <Avatar name={m.name} color={m.color} photoUrl={m.avatarUrl} size={26} />
                  <span className="text-[13px]" style={{ color: on ? m.color : 'var(--color-text-secondary)' }}>
                    {m.name}
                  </span>
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Place">
          <Input
            placeholder="Optional"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            autoCapitalize="words"
          />
        </Field>

        <Field label="Notes">
          <Textarea rows={2} placeholder="Optional" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>

        <Field label="Colour" hint="Auto uses the category, then the first person.">
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              onClick={() => setColor(null)}
              className={cx(
                'h-8 rounded-md border px-3 font-mono text-[12px] uppercase transition-colors',
                color === null ? 'border-accent text-accent' : 'border-border text-muted',
              )}
              style={{ letterSpacing: '0.12em' }}
            >
              Auto
            </button>
            {MEMBER_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className="size-8 rounded-full transition-transform"
                style={{
                  background: c,
                  outline: color === c ? `2px solid ${c}` : 'none',
                  outlineOffset: 3,
                }}
                aria-label={`Colour ${c}`}
              />
            ))}
          </div>
        </Field>

        {error && <p className="font-mono text-[12px] text-danger">{error}</p>}

        {isEdit && !readOnly && instance?.eventId && <EventComments eventId={instance.eventId} />}
      </div>

      {scopePrompt && (
        <ScopePrompt
          onThis={() => {
            setScopePrompt(false);
            detach.mutate();
          }}
          onAll={() => {
            setScopePrompt(false);
            save.mutate();
          }}
          onCancel={() => setScopePrompt(false)}
        />
      )}
    </Modal>
  );
}

/** Asks whether an edit to a repeating event applies to just this day or the whole series. */
function ScopePrompt({
  onThis,
  onAll,
  onCancel,
}: {
  onThis: () => void;
  onAll: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)' }}
      onMouseDown={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="w-full max-w-sm overflow-hidden rounded-lg border border-border bg-surface-high shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
        <div className="px-5 py-4">
          <h2 className="font-display text-[18px] text-primary">This is a repeating event</h2>
          <p className="mt-1.5 text-[14px] leading-relaxed text-secondary">Save your changes to…</p>
        </div>
        <div className="flex flex-col gap-2 px-5 pb-5">
          <Button variant="primary" size="lg" onClick={onThis}>
            This event only
          </Button>
          <Button variant="secondary" size="lg" onClick={onAll}>
            All events in the series
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
